<template>
  <ManagerLayout>
    <div class="manager-individual-analysis">
      <h2 class="page-title">個別分析送付</h2>
      <p class="page-desc">
        Day11〜12の個別分析レポートを録音し、「送信予約」すると Worker が音声変換のうえ、所定の送付窓（JST・環境変数で設定）に保護者LINEへ送ります。
        提出率は Day1〜11 の練習・課題の両方が揃った日数÷11（%）です。LINE案内どおり 60％以上が送付の目安です。
      </p>

      <section class="manager-section filters-section">
        <div class="card">
          <h3 class="section-title">検索</h3>
          <div class="filters">
            <label class="filter-item filter-search">
              <span class="filter-label">学習者名</span>
              <input
                v-model="searchQ"
                type="text"
                placeholder="部分一致"
                class="form-input"
                @keyup.enter="loadLearners"
              />
            </label>
            <label class="filter-item filter-check">
              <input v-model="includeArchived" type="checkbox" @change="loadLearners" />
              <span class="filter-label">アーカイブ済みを表示</span>
            </label>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="loadLearners">
              {{ loading ? '読み込み中...' : '検索' }}
            </button>
          </div>
        </div>
      </section>

      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <span class="total-note">全 {{ total }} 件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="loadLearners">
              更新
            </button>
          </div>

          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!learners.length" class="empty-state">該当する学習者がいません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="個別分析送付 学習者一覧">
              <thead>
                <tr>
                  <th>学習者</th>
                  <th>生年月日・学年</th>
                  <th>初回サブスク（JST）</th>
                  <th class="num">進行日</th>
                  <th class="num">提出率（1〜11）</th>
                  <th>メイン契約</th>
                  <th>候補</th>
                  <th>Day11</th>
                  <th>Day12</th>
                  <th class="action">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="l in learners"
                  :key="l.id"
                  class="data-row"
                  :class="{ 'row-priority': l.needsReportByRate, 'row-archived': l.listHidden }"
                >
                  <td>
                    <div class="cell-main">
                      <span class="cell-title">{{ l.name || '—' }}</span>
                      <span class="cell-sub cell-kana">{{ l.nameKana || '—' }}</span>
                      <span class="cell-sub">保護者: {{ l.parentName || '—' }}</span>
                      <span v-if="l.listHidden" class="cell-sub archive-tag">アーカイブ</span>
                    </div>
                  </td>
                  <td>
                    <span class="sub-label tight">{{ l.birthDateGradeDisplayJa || '—' }}</span>
                  </td>
                  <td>
                    <div v-if="l.firstSubscriptionStartedDisplayJa" class="sub-label tight">
                      <div>{{ l.firstSubscriptionStartedDisplayJa }}</div>
                      <div v-if="l.dayNumberSinceFirstSubscription != null" class="rate-sub">
                        今日で {{ l.dayNumberSinceFirstSubscription }} 日目（JST）
                      </div>
                    </div>
                    <span v-else class="sub-label tight">—</span>
                  </td>
                  <td class="num">
                    <span class="rate-main">Day {{ l.currentDay ?? '—' }}</span>
                    <span class="rate-sub">プログラム上の進行日</span>
                  </td>
                  <td class="num">
                    <span class="rate-main">{{ l.submissionRatePercent ?? '—' }}%</span>
                    <span class="rate-sub">（{{ l.completedDays1to11 ?? '—' }}/{{ l.submissionRateDenominator ?? 11 }}日）</span>
                  </td>
                  <td>
                    <span class="sub-label" :title="subscriptionTitle(l)">{{ l.mainSubscriptionLabelJa || '—' }}</span>
                  </td>
                  <td>
                    <span v-if="l.needsReportByRate" class="badge badge-priority">60％以上</span>
                    <span v-else class="badge badge-muted">—</span>
                  </td>
                  <td>
                    <span :class="['badge', listDayBadge(l, 11).cls]">{{ listDayBadge(l, 11).label }}</span>
                  </td>
                  <td>
                    <span :class="['badge', listDayBadge(l, 12).cls]">{{ listDayBadge(l, 12).label }}</span>
                  </td>
                  <td class="action action-col">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline"
                      :disabled="detailLoading && selectedLearnerId === l.id"
                      @click="openDetail(l.id)"
                    >
                      {{ detailLoading && selectedLearnerId === l.id ? '読み込み中...' : '表示' }}
                    </button>
                    <button
                      v-if="!l.listHidden"
                      type="button"
                      class="btn btn-sm btn-secondary"
                      :disabled="archiveLoadingId === l.id"
                      @click="setListVisibility(l.id, true)"
                    >
                      {{ archiveLoadingId === l.id ? '…' : 'アーカイブ' }}
                    </button>
                    <button
                      v-else
                      type="button"
                      class="btn btn-sm btn-secondary"
                      :disabled="archiveLoadingId === l.id"
                      @click="setListVisibility(l.id, false)"
                    >
                      {{ archiveLoadingId === l.id ? '…' : '一覧に戻す' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section v-if="selectedLearner" class="manager-section">
        <div class="card detail-card">
          <h3 class="section-title">詳細（{{ selectedLearner.name }}）</h3>

          <div class="detail-meta">
            <div>氏名: {{ selectedLearner.name || '—' }}</div>
            <div class="detail-kana">{{ selectedLearner.nameKana || '—' }}</div>
            <div>生年月日・学年: {{ selectedLearner.birthDateGradeDisplayJa || '—' }}</div>
            <div>保護者: {{ selectedLearner.parentName || '—' }}</div>
            <div>
              初回サブスク（JST）: {{ selectedLearner.firstSubscriptionStartedDisplayJa || '—' }}
              <span
                v-if="selectedLearner.firstSubscriptionStartedDisplayJa && selectedLearner.dayNumberSinceFirstSubscription != null"
                class="detail-sub"
              >
                （今日で {{ selectedLearner.dayNumberSinceFirstSubscription }} 日目）
              </span>
            </div>
            <div>進行日: {{ selectedLearner.programDayLabel || `Day ${selectedLearner.currentDay ?? '—'}` }}</div>
            <div>
              提出率（1〜11）: {{ selectedLearner.submissionRatePercent ?? '—' }}%
              （{{ selectedLearner.completedDays1to11 ?? '—' }}/{{ selectedLearner.submissionRateDenominator ?? '—' }}日）
              <span v-if="selectedLearner.needsReportByRate" class="badge badge-priority inline">送付候補（60％以上）</span>
            </div>
            <div>メイン契約: {{ selectedLearner.mainSubscriptionLabelJa || '—' }}</div>
            <div v-if="selectedLearner.mainTrialEnd" class="detail-sub">
              体験終了目安: {{ formatDateTime(selectedLearner.mainTrialEnd) }}
            </div>
            <div v-if="selectedLearner.mainCurrentPeriodEnd" class="detail-sub">
              請求期間終了: {{ formatDateTime(selectedLearner.mainCurrentPeriodEnd) }}
            </div>
          </div>

          <div class="sub-section">
            <h4 class="sub-title">Day11までの Assignment 音声</h4>
            <div v-if="detail.assignmentAudios?.length">
              <div
                v-for="a in detail.assignmentAudios"
                :key="a.submissionId"
                class="audio-block"
              >
                <div class="audio-row-label">Day{{ a.day }}</div>
                <div class="audio-block-body">
                  <div v-if="a.submittedAtDisplayJa" class="submit-meta">
                    <span class="submit-meta-label">課題提出</span>
                    <span class="submit-meta-datetime">{{ a.submittedAtDisplayJa }}</span>
                    <span
                      class="badge"
                      :class="a.submittedCalendarKindJa === '平日' ? 'badge-weekday' : 'badge-holiday'"
                    >
                      {{ a.submittedCalendarKindJa || '—' }}
                    </span>
                  </div>
                  <div v-else class="submit-meta muted">提出日時が取得できません</div>
                  <audio class="audio-player" :src="a.signedUrl" controls preload="none" />
                </div>
              </div>
            </div>
            <div v-else class="empty-state small">Day11までの Assignment 音声が見つかりません</div>
          </div>

          <div class="sub-section grid-two">
            <div class="day-card">
              <h4 class="sub-title">Day11</h4>
              <div class="status-line">
                <span :class="['badge', detailDayBadge(11).cls]">{{ detailDayBadge(11).label }}</span>
              </div>
              <div v-if="detail.reportsByDay?.[11]?.lineSendRequestedAt" class="hint tight">
                予約: {{ formatDateTime(detail.reportsByDay[11].lineSendRequestedAt) }}
              </div>
              <div v-if="detail.reportsByDay?.[11]?.conversionError" class="hint error-text">
                変換: {{ detail.reportsByDay[11].conversionError }}
              </div>
              <div v-if="detail.reportsByDay?.[11]?.lineError" class="hint error-text">
                LINE: {{ detail.reportsByDay[11].lineError }}
              </div>

              <div v-if="detail.reportsByDay?.[11]?.audioSignedUrl" class="recorded-audio">
                <audio class="audio-player" :src="detail.reportsByDay[11].audioSignedUrl" controls preload="none" />
              </div>

              <div class="record-controls">
                <button
                  class="btn btn-primary"
                  :disabled="detailLoading || scheduleBusy || !canRecordDay(11)"
                  @click="startRecording(11)"
                >
                  {{ isRecordingDay === 11 ? '録音中...' : '録音' }}
                </button>
                <button
                  class="btn btn-secondary"
                  :disabled="detailLoading || scheduleBusy || isRecordingDay !== 11"
                  @click="stopRecording(11)"
                >
                  停止
                </button>
                <button
                  class="btn btn-success"
                  :disabled="detailLoading || scheduleBusy || !canScheduleDay(11)"
                  @click="scheduleSendDay(11)"
                >
                  送信予約
                </button>
                <button
                  type="button"
                  class="btn btn-outline"
                  :disabled="detailLoading || scheduleBusy || !canCancelScheduleDay(11)"
                  @click="cancelScheduleDay(11)"
                >
                  予約取消
                </button>
              </div>

              <div class="hint">
                録音保存のあと「送信予約」を押すと、Worker が変換し所定の窓で LINE 送付します。
              </div>
            </div>

            <div class="day-card">
              <h4 class="sub-title">Day12</h4>
              <div class="status-line">
                <span :class="['badge', detailDayBadge(12).cls]">{{ detailDayBadge(12).label }}</span>
              </div>
              <div v-if="detail.reportsByDay?.[12]?.lineSendRequestedAt" class="hint tight">
                予約: {{ formatDateTime(detail.reportsByDay[12].lineSendRequestedAt) }}
              </div>
              <div v-if="detail.reportsByDay?.[12]?.conversionError" class="hint error-text">
                変換: {{ detail.reportsByDay[12].conversionError }}
              </div>
              <div v-if="detail.reportsByDay?.[12]?.lineError" class="hint error-text">
                LINE: {{ detail.reportsByDay[12].lineError }}
              </div>

              <div v-if="detail.reportsByDay?.[12]?.audioSignedUrl" class="recorded-audio">
                <audio class="audio-player" :src="detail.reportsByDay[12].audioSignedUrl" controls preload="none" />
              </div>

              <div class="record-controls">
                <button
                  class="btn btn-primary"
                  :disabled="detailLoading || scheduleBusy || !canRecordDay(12)"
                  @click="startRecording(12)"
                >
                  {{ isRecordingDay === 12 ? '録音中...' : '録音' }}
                </button>
                <button
                  class="btn btn-secondary"
                  :disabled="detailLoading || scheduleBusy || isRecordingDay !== 12"
                  @click="stopRecording(12)"
                >
                  停止
                </button>
                <button
                  class="btn btn-success"
                  :disabled="detailLoading || scheduleBusy || !canScheduleDay(12)"
                  @click="scheduleSendDay(12)"
                >
                  送信予約
                </button>
                <button
                  type="button"
                  class="btn btn-outline"
                  :disabled="detailLoading || scheduleBusy || !canCancelScheduleDay(12)"
                  @click="cancelScheduleDay(12)"
                >
                  予約取消
                </button>
              </div>

              <div class="hint">
                Day12 も同様です。
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const PAGE_SIZE = 200

const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'

const searchQ = ref('')
const includeArchived = ref(false)
const learners = ref([])
const total = ref(0)
const loading = ref(false)
const archiveLoadingId = ref(null)

const currentOffset = ref(0)

const selectedLearnerId = ref(null)
const selectedLearner = ref(null)
const detail = ref({
  assignmentAudios: [],
  reportsByDay: {}
})
const detailLoading = ref(false)

const scheduleBusy = ref(false)

// --- 録音（音声を吹き込む） ---
const isRecordingDay = ref(null)
let mediaRecorder = null
let chunks = []
let elapsedSeconds = 0
let timer = null
const recordedBlob = ref(null)
const recordedDurationMs = ref(0)

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatDateTime(iso) {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })
  } catch (_) {
    return iso
  }
}

function deliveryOfReport (day) {
  const r = detail.value?.reportsByDay?.[day]
  const ds = r?.deliveryStatus ?? null
  const sent = !!r?.sentAt
  const recorded = !!r?.recordedAt
  return { r, ds, sent, recorded }
}

function listDayBadge (learner, day) {
  const sent = day === 11 ? learner.day11SentAt : learner.day12SentAt
  const rec = day === 11 ? learner.day11RecordedAt : learner.day12RecordedAt
  const st = day === 11 ? learner.day11DeliveryStatus : learner.day12DeliveryStatus
  if (sent) return { label: '送付済み', cls: 'badge-success' }
  if (st === 'failed_line' || st === 'failed_conversion') return { label: '要対応', cls: 'badge-danger' }
  if (st === 'send_queued' || st === 'converting' || st === 'converted' || st === 'sending') {
    const map = {
      send_queued: '予約済',
      converting: '変換中',
      converted: '送付待ち',
      sending: '送信中'
    }
    return { label: map[st] || st, cls: 'badge-warning' }
  }
  if (rec) return { label: '下書き', cls: 'badge-muted' }
  return { label: '未対応', cls: 'badge-muted' }
}

function detailDayBadge (day) {
  const { ds, sent, recorded } = deliveryOfReport(day)
  if (sent) return { label: '送付済み', cls: 'badge-success' }
  if (ds === 'failed_line') return { label: 'LINE失敗', cls: 'badge-danger' }
  if (ds === 'failed_conversion') return { label: '変換失敗', cls: 'badge-danger' }
  if (ds === 'send_queued') return { label: '送信予約済（変換待ち）', cls: 'badge-warning' }
  if (ds === 'converting') return { label: '変換中', cls: 'badge-warning' }
  if (ds === 'converted') return { label: '送付待ち（所定の窓）', cls: 'badge-warning' }
  if (ds === 'sending') return { label: '送信中', cls: 'badge-warning' }
  if (recorded) return { label: '下書き（未予約）', cls: 'badge-muted' }
  return { label: '未録音', cls: 'badge-muted' }
}

function canRecordDay (day) {
  const { ds, sent } = deliveryOfReport(day)
  if (sent) return false
  if (ds === 'converting' || ds === 'sending') return false
  if (isRecordingDay.value != null && isRecordingDay.value !== day) return false
  return true
}

function canScheduleDay (day) {
  const { ds, sent, recorded } = deliveryOfReport(day)
  if (!recorded || sent) return false
  const ok = new Set(['draft', 'failed_conversion', 'failed_line', 'converted'])
  return ok.has(ds || 'draft')
}

function canCancelScheduleDay (day) {
  const { ds, sent } = deliveryOfReport(day)
  if (sent) return false
  const ok = new Set(['send_queued', 'converted', 'failed_conversion', 'failed_line'])
  return ok.has(ds || '')
}

function subscriptionTitle (l) {
  const parts = [l.mainSubscriptionLabelJa].filter(Boolean)
  if (l.mainTrialEnd) parts.push(`体験終了目安: ${formatDateTime(l.mainTrialEnd)}`)
  if (l.mainCurrentPeriodEnd) parts.push(`請求期間終了: ${formatDateTime(l.mainCurrentPeriodEnd)}`)
  if (l.mainCancelAtPeriodEnd) parts.push('解約予定あり')
  return parts.join(' / ') || ''
}

async function loadLearners () {
  loading.value = true
  try {
    const token = await getToken()
    if (!token) throw new Error('認証が必要です')

    const qs = new URLSearchParams()
    qs.set('limit', String(PAGE_SIZE))
    qs.set('offset', String(currentOffset.value))
    if (searchQ.value.trim()) qs.set('q', searchQ.value.trim())
    if (includeArchived.value) qs.set('includeArchived', '1')

    const res = await fetch(`${apiBase}/api/manager/individual-analysis/learners?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json.error || json.message || '学習者一覧の取得に失敗しました')

    learners.value = json.learners || []
    total.value = json.total || learners.value.length
  } finally {
    loading.value = false
  }
}

async function setListVisibility (learnerId, hidden) {
  archiveLoadingId.value = learnerId
  try {
    const token = await getToken()
    if (!token) throw new Error('認証が必要です')

    const res = await fetch(
      `${apiBase}/api/manager/individual-analysis/learners/${encodeURIComponent(learnerId)}/list-visibility`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hidden })
      }
    )
    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json.error || json.message || '更新に失敗しました')

    if (selectedLearnerId.value === learnerId && hidden) {
      selectedLearnerId.value = null
      selectedLearner.value = null
    }
    await loadLearners()
  } catch (e) {
    alert(e.message || 'エラーが発生しました')
  } finally {
    archiveLoadingId.value = null
  }
}

async function openDetail (learnerId) {
  selectedLearnerId.value = learnerId
  detailLoading.value = true
  try {
    const token = await getToken()
    if (!token) throw new Error('認証が必要です')

    const res = await fetch(`${apiBase}/api/manager/individual-analysis/learners/${encodeURIComponent(learnerId)}/detail?assignmentDayMax=11`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json.error || json.message || '詳細の取得に失敗しました')

    selectedLearner.value = json.learner
    detail.value = {
      assignmentAudios: json.assignmentAudios || [],
      reportsByDay: json.reportsByDay || {}
    }
    recordedBlob.value = null
    recordedDurationMs.value = 0
    isRecordingDay.value = null
  } finally {
    detailLoading.value = false
  }
}

function ensureMediaRecorderSupported () {
  if (typeof MediaRecorder === 'undefined') {
    throw new Error('このブラウザでは録音に対応していません（MediaRecorderが利用できません）')
  }
}

async function startRecording (day) {
  if (!selectedLearnerId.value) return
  ensureMediaRecorderSupported()
  if (isRecordingDay.value != null) return

  if (!canRecordDay(day)) return

  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  chunks = []
  elapsedSeconds = 0
  recordedBlob.value = null
  recordedDurationMs.value = 0

  // mimeType は未指定でも動く（iOS/Safari で落ちる場合は後で拡張）
  mediaRecorder = new MediaRecorder(stream)
  isRecordingDay.value = day

  mediaRecorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data)
  }

  mediaRecorder.onstop = () => {
    try {
      const blob = new Blob(chunks, { type: mediaRecorder.mimeType || 'audio/webm' })
      recordedBlob.value = blob
      // 録音時間（概算）
      recordedDurationMs.value = Math.max(1, elapsedSeconds * 1000)
    } catch (e) {
      console.error('録音Blob生成エラー:', e)
    } finally {
      // ストリーム停止（不要なマイク利用を止める）
      try { stream.getTracks().forEach(t => t.stop()) } catch (_) {}
      isRecordingDay.value = null
    }
  }

  mediaRecorder.start()
  timer = setInterval(() => {
    elapsedSeconds++
  }, 1000)
}

async function stopRecording (day) {
  if (!mediaRecorder || isRecordingDay.value !== day) return
  clearInterval(timer)
  timer = null

  try {
    mediaRecorder.stop()
  } catch (e) {
    console.error('stopRecording error:', e)
  }

  // stop() 後に Blob を作るので、少し待ってからアップロードする
  await new Promise(resolve => setTimeout(resolve, 500))
  await uploadRecordedAudio(day)
}

async function uploadRecordedAudio (day) {
  const blob = recordedBlob.value
  if (!blob) {
    alert('録音した音声が見つかりませんでした。もう一度録音してください。')
    return
  }

  detailLoading.value = true
  try {
    const token = await getToken()
    if (!token) throw new Error('認証が必要です')

    const formData = new FormData()
    formData.append('learner_id', selectedLearnerId.value)
    formData.append('day', String(day))
    formData.append('durationMs', String(recordedDurationMs.value || 0))
    formData.append('audio', blob, `individual-analysis-day${day}.webm`)

    const res = await fetch(`${apiBase}/api/manager/individual-analysis/records`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    })

    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json.error || json.message || '音声の保存に失敗しました')

    await openDetail(selectedLearnerId.value)
  } finally {
    detailLoading.value = false
  }
}

async function scheduleSendDay (day) {
  if (!selectedLearnerId.value) return
  if (!canScheduleDay(day)) return

  scheduleBusy.value = true
  try {
    const token = await getToken()
    if (!token) throw new Error('認証が必要です')

    const res = await fetch(`${apiBase}/api/manager/individual-analysis/schedule-send`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        learner_id: selectedLearnerId.value,
        day
      })
    })

    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json.error || json.message || '送信予約に失敗しました')

    alert('送信を予約しました。Worker が変換し、所定の送付窓で LINE 送付します。')
    await openDetail(selectedLearnerId.value)
    await loadLearners()
  } finally {
    scheduleBusy.value = false
  }
}

async function cancelScheduleDay (day) {
  if (!selectedLearnerId.value) return
  if (!canCancelScheduleDay(day)) return
  if (!confirm('送信予約を取り消しますか？')) return

  scheduleBusy.value = true
  try {
    const token = await getToken()
    if (!token) throw new Error('認証が必要です')

    const res = await fetch(`${apiBase}/api/manager/individual-analysis/cancel-scheduled-send`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        learner_id: selectedLearnerId.value,
        day
      })
    })

    const json = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(json.error || json.message || '予約取消に失敗しました')

    await openDetail(selectedLearnerId.value)
    await loadLearners()
  } finally {
    scheduleBusy.value = false
  }
}

// 初期ロード
loadLearners()
</script>

<style scoped>
.manager-individual-analysis .page-title {
  margin-bottom: 0.5rem;
}

.filters {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-check {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding-bottom: 0.15rem;
}

.filter-check input {
  width: 1rem;
  height: 1rem;
}

.table-wrap {
  overflow: auto;
}

.cell-main {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.cell-title {
  font-weight: 700;
}

.cell-sub {
  color: #64748b;
  font-size: 0.9rem;
}

.cell-kana {
  font-size: 0.88rem;
}

.detail-kana {
  color: #475569;
  font-size: 0.95rem;
}

.archive-tag {
  display: inline-block;
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: #64748b;
}

.rate-main {
  font-weight: 700;
  color: #0f172a;
}

.rate-sub {
  display: block;
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 400;
}

.sub-label {
  font-size: 0.88rem;
  color: #0f172a;
  line-height: 1.35;
}

.badge-priority {
  background: rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
}

.badge-priority.inline {
  margin-left: 0.35rem;
  vertical-align: middle;
}

.data-row.row-priority {
  background: rgba(239, 246, 255, 0.65);
}

.data-row.row-archived {
  opacity: 0.92;
}

.action-col {
  white-space: nowrap;
}

.action-col .btn {
  margin-right: 0.35rem;
  margin-bottom: 0.2rem;
}

.detail-sub {
  font-size: 0.9rem;
  color: #475569;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
}

.badge-success {
  background: rgba(16, 185, 129, 0.15);
  color: #0f766e;
}

.badge-warning {
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
}

.badge-muted {
  background: rgba(148, 163, 184, 0.2);
  color: #334155;
}

.badge-danger {
  background: rgba(239, 68, 68, 0.15);
  color: #b91c1c;
}

.hint.tight {
  margin-top: 0.25rem;
  font-size: 0.82rem;
}

.hint.error-text {
  color: #b91c1c;
  font-size: 0.82rem;
}

.detail-card {
  padding: 1rem;
}

.detail-meta {
  display: grid;
  gap: 0.4rem;
  margin-bottom: 1rem;
  color: #0f172a;
}

.sub-section {
  margin-top: 1rem;
}

.grid-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.day-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.9rem;
}

.audio-block {
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 0.75rem;
  align-items: start;
  margin: 0.65rem 0;
}

.audio-block-body {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;
}

.submit-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.6rem;
  font-size: 0.88rem;
  color: #0f172a;
  line-height: 1.4;
}

.submit-meta.muted {
  color: #94a3b8;
}

.submit-meta-label {
  font-weight: 700;
  color: #475569;
}

.submit-meta-datetime {
  font-variant-numeric: tabular-nums;
}

.badge-weekday {
  background: rgba(148, 163, 184, 0.25);
  color: #334155;
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 700;
}

.badge-holiday {
  background: rgba(251, 191, 36, 0.25);
  color: #92400e;
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 700;
}

.audio-row-label {
  font-weight: 700;
  color: #0f172a;
  padding-top: 0.35rem;
}

.sub-label.tight {
  font-size: 0.82rem;
  line-height: 1.35;
}

.audio-player {
  width: 100%;
}

.record-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.75rem;
}

.hint {
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 0.6rem;
}

.empty-state.small {
  font-size: 0.95rem;
  color: #64748b;
}

@media (max-width: 900px) {
  .grid-two {
    grid-template-columns: 1fr;
  }
}
</style>

